import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Form, message, Radio, Spin } from "antd";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../../Assets/Images/logo.svg";
import logoCompany from "../../../Assets/Icons/companylogo.svg";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../Components/Cards/InputCard";
import ModuleSelctorCards from "../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../Assets/Icons/car_repair.svg";
import {
  getFleetManagerRevisionRequest,
  submitFleetManagerRevision,
  deleteFleetManagerRevisionDocument,
} from "../../../API/Public/FleetManagerRevise";
import { getModules, getDocumentCategories } from "../../../API/Config";
import { getToken } from "../../../Utlis/UpdateUserState";
import { navixyApiKeyFieldProps } from "../../../Utlis/NavixyApiKeys";
import "./style.css";

const MODULE_ICONS = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const FleetManagerRevise = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [vehicles, setVehicles] = useState(null);
  const [modules, setModules] = useState([]);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [documentCategories, setDocumentCategories] = useState([]);
  const isAnnual = pricingPeriod === "Annually Pricing";

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchModules = async () => {
      const res = await getModules();
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setModules(list);
      }
    };

    fetchModules();
  }, []);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fleet-manager");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  useEffect(() => {
    if (!token && !getToken()) {
      setLoadError("This revision link is invalid or missing a token.");
      setLoading(false);
      return;
    }

    const fetchRequest = async () => {
      try {
        const res = await getFleetManagerRevisionRequest(token);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          const entity = data.entity || {};

          form.setFieldsValue({
            legalBusinessName: entity.legal_name || "",
            registrationNumber: entity.registration_number || "",
            contactNumber: entity.registration_contact_phone || "",
            currency: entity.currency || "JMD",
            headQuarterAddress: entity.address || "",
            apiKeys: entity.navixy_api_keys || [],
            dateIncorporated: entity.date_incorporated
              ? dayjs(entity.date_incorporated)
              : undefined,
            primarycontactperson: entity.primary_contact_person || "",
            designation: entity.designation || "",
            businessemail: entity.contact_email || "",
            phonenumber: entity.contact_phone || "",
            secondarycontactperson: entity.secondary_contact_person || "",
            secondaryDesignation: entity.secondary_designation || "",
            secondaryEmail: entity.secondary_contact_email || "",
            secondaryPhone: entity.secondary_contact_phone || "",
          });

          setAvatar(resolveFileUrl(entity.avatar));
          setVehicles(entity.vehicle_limit ?? null);
          setSelectedModuleIds(
            Array.isArray(entity.assigned_modules)
              ? entity.assigned_modules.map((mod) => mod.id ?? mod)
              : [],
          );
          setDocuments(
            Array.isArray(entity.documents)
              ? entity.documents.map((doc) => ({
                  id: doc.id,
                  key: String(doc.id),
                  name:
                    doc.document_name ||
                    (doc.file ? doc.file.split("/").pop() : doc.category) ||
                    "-",
                  category: doc.category || "-",
                  allowedFormats: doc.allowed_formats || [],
                  date: doc.uploaded_at
                    ? new Date(doc.uploaded_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "-",
                  expiryDate: doc.expiry_date || null,
                  fileUrl: resolveFileUrl(doc.file),
                }))
              : [],
          );
          setReviewNotes(data.audit_notes || "");
        } else {
          setLoadError(
            getErrorMessage(res?.data, "Unable to load this revision request."),
          );
        }
      } catch (err) {
        setLoadError(
          getErrorMessage(
            err?.response?.data,
            "Unable to load this revision request.",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const planOptions = modules.map((module) => {
    const isInactive = module.status !== "active";

    return {
      id: module.id,
      title: module.name,
      description: module.description,
      price: `${module.currency || "JMD"} ${isAnnual ? module.annual_price_per_vehicle : module.monthly_price_per_vehicle}/vehicle`,
      icon: (
        <img src={MODULE_ICONS[module.code] || FuelIcon} alt={module.code} />
      ),
      disabled: isInactive,
      tag: isInactive ? "Coming Soon" : undefined,
    };
  });

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = () => setAvatar(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedModuleIds.length) {
        message.error("Please select at least one module.");
        return;
      }
      if (!vehicles || vehicles < 1) {
        message.error("Please enter number of vehicles.");
        return;
      }

      const formData = new FormData();
      formData.append("legal_name", values.legalBusinessName);
      formData.append("registration_number", values.registrationNumber);
      formData.append("contact_email", values.businessemail);
      formData.append("contact_phone", values.phonenumber);
      formData.append("registration_contact_phone", values.contactNumber);
      formData.append("address", values.headQuarterAddress);
      formData.append("navixy_api_keys", JSON.stringify(values.apiKeys || []));
      formData.append("currency", values.currency);
      formData.append(
        "date_incorporated",
        values.dateIncorporated ? values.dateIncorporated.format("YYYY-MM-DD") : "",
      );
      formData.append("primary_contact_person", values.primarycontactperson);
      formData.append("designation", values.designation);
      formData.append(
        "secondary_contact_person",
        values.secondarycontactperson,
      );
      formData.append("secondary_designation", values.secondaryDesignation);
      formData.append("secondary_contact_email", values.secondaryEmail);
      formData.append("secondary_contact_phone", values.secondaryPhone);
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }
      formData.append("vehicle_limit", vehicles);
      formData.append("billing_cycle", isAnnual ? "annual" : "monthly");
      selectedModuleIds.forEach((id) => {
        formData.append("assigned_modules_ids", id);
      });

      const newDocuments = documents.filter((doc) => doc.file);
      newDocuments.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      if (newDocuments.length > 0) {
        formData.append(
          "document_categories",
          JSON.stringify(newDocuments.map((doc) => doc.category)),
        );
        formData.append(
          "document_expiry_dates",
          JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
        );
      }

      setSubmitting(true);
      const res = await submitFleetManagerRevision(token, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || "Your revised application has been submitted.",
        );
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login-manager";
        }, 1500);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to submit revised application."),
        );
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to submit revised application.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const registrationInformation = [
    {
      name: "legalBusinessName",
      label: "Legal Business Name",
      type: "text",
      placeholder: "Enter Legal Business Name",
      rules: [{ required: true, message: "Business name is required" }],
    },
    {
      name: "registrationNumber",
      label: "Registration Number",
      type: "text",
      placeholder: "Enter Registration Number",
      rules: [{ required: true, message: "Registration number is required" }],
    },
    {
      name: "contactNumber",
      label: "Company Contact Number",
      type: "phone",
      placeholder: "Enter Contact Number",
      rules: [{ required: true, message: "Contact number is required" }],
    },
    {
      name: "currency",
      label: "Currency",
      type: "currency",
      placeholder: "Enter amount",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "headQuarterAddress",
      label: "Headquarters Address",
      type: "textarea",
      fullWidth: true,
      placeholder: "Enter Address Of Headquarters",
      rules: [{ required: true, message: "Address is required" }],
    },
    {
      name: "dateIncorporated",
      label: "Date Incorporated",
      type: "date",
      placeholder: "Select Date Incorporated",
      rules: [{ required: true, message: "Date incorporated is required" }],
    },
    {
      name: "apiKeys",
      label: "API Key",
      placeholder: "Enter API key",
      type: "multi-input",
      addLabel: "Add another API key",
      ...navixyApiKeyFieldProps,
      fullWidth: true,
    },
  ];

  const contactInfoFields = [
    {
      name: "primarycontactperson",
      label: "Primary Contact Person",
      type: "text",
      placeholder: "Enter Name Of Primary Contact Person",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "designation",
      label: "Designation",
      type: "text",
      placeholder: "Enter Designation Of Primary Contact Person",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "businessemail",
      label: "Business Email",
      type: "text",
      placeholder: "Enter Email Of Primary Contact Person",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "phonenumber",
      label: "Phone Number",
      type: "phone",
      placeholder: "Enter Contact Number Of Primary Contact Person",
      rules: [{ required: true, message: "Phone number is required" }],
    },
  ];

  const secondaryContactInformationFields = [
    {
      name: "secondarycontactperson",
      label: "Secondary Contact Person",
      type: "text",
      placeholder: "Enter Name of Secondary Contact Person",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "secondaryDesignation",
      label: "Designation",
      type: "text",
      placeholder: "Enter Designation of Secondary Contact Person",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "secondaryEmail",
      label: "Business Email",
      type: "text",
      placeholder: "Enter Buisness Email Of Secondary Contact Person",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "secondaryPhone",
      label: "Phone Number",
      type: "phone",
      placeholder: "Enter Contact Number of Secondary Contact Person",
      rules: [{ required: true, message: "Phone is required" }],
    },
  ];

  const handleVehicleChange = (value) => {
    setVehicles(value === null || value === undefined ? null : Number(value));
  };

  if (loading) {
    return (
      <div
        className="fm-revise fm-revise--status"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fm-revise fm-revise--status">
        <img src={logo} alt="logo" className="fm-revise__logo" />
        <div className="fm-revise__status-text fm-revise__status-text--error">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="sign-up fm-revise">
      <div className="logo">
        <img src={logo} alt="logo" />
      </div>
      <div className="heading">Revise Your Onboarding Request</div>

      {reviewNotes && (
        <div className="fm-revise__notice">
          <InfoCircleOutlined className="fm-revise__notice-icon" />
          <div>
            <div className="fm-revise__notice-title">Reviewer Feedback</div>
            <div className="fm-revise__notice-text">{reviewNotes}</div>
          </div>
        </div>
      )}

      <div className="signup-card">
        <Form form={form} layout="vertical">
          <div className="row">
            <div className="profile-card">
              <div className="profile-header">
                <div className="btn">
                  <Button
                    type="default"
                    className="upload-btn"
                    onClick={handleButtonClick}
                  >
                    Upload Photo
                  </Button>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <div className="profile-avatar">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt="avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    "T"
                  )}
                </div>
              </div>
              <div className="profile-body"></div>
            </div>
          </div>

          <InputCard
            title="Registration Information"
            fields={registrationInformation}
          />
          <InputCard title="Contact Information" fields={contactInfoFields} />
          <InputCard
            title="Secondary Contact Information"
            fields={secondaryContactInformationFields}
          />

          <ModuleSelctorCards
            options={planOptions}
            multiple={true}
            selectedIds={selectedModuleIds}
            onChange={setSelectedModuleIds}
            showVehicleInput={true}
            vehicleCount={vehicles}
            onVehicleChange={handleVehicleChange}
            showFooter={true}
            footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
            headerExtra={
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                className="custom-segmented-control"
                value={pricingPeriod}
                onChange={(e) => setPricingPeriod(e.target.value)}
                options={PRICING_TABS.map((tab) => ({
                  label: tab.replace(" Pricing", ""),
                  value: tab,
                }))}
              />
            }
          />

          <GenericDocumentManagerTable
            setDocuments={setDocuments}
            initialDocuments={documents}
            documentTypes={documentCategories}
            onDeleteDocument={async (doc) => {
              if (!doc?.id) return;

              const res = await deleteFleetManagerRevisionDocument(
                token,
                doc.id,
              );

              if (Math.floor(res?.status / 100) !== 2) {
                throw new Error(
                  getErrorMessage(res?.data, "Failed to delete document."),
                );
              }
            }}
          />

          <div className="btns-row">
            <Button
              children="Back"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />
            <Button
              children="Resubmit Application"
              onClick={handleSubmit}
              loading={submitting}
              className="save-btn"
            />
          </div>
        </Form>

        <div className="logo-wrapper">
          <img src={logoCompany} alt="Logo Icon" className="logo-icon" />
          <div className="text">
            Powered by <span>CouSync</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetManagerRevise;
